import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Replace, Loader2, User, Search, Clock, AlertTriangle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

export default function ReplaceStaffDialog({ open, onOpenChange, group, oldStaff, allUsers = [], onReplaced }) {
  const [oldSchedule, setOldSchedule] = useState(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pull the OLD staff's schedule so admin sees exactly what's being transferred
  const fetchOldSchedule = useCallback(async () => {
    if (!group?.shifts?.length || !oldStaff?.user_id) return;
    setLoading(true);
    try {
      const ids = group.shifts.map((s) => s.id).join(',');
      const res = await axios.get(`${API}/admin/event-schedule`, { params: { open_shift_ids: ids } });
      const row = (res.data?.staff || []).find((s) => s.user_id === oldStaff.user_id);
      setOldSchedule({ event: res.data?.event, row });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load schedule');
    } finally {
      setLoading(false);
    }
  }, [group, oldStaff]);

  useEffect(() => {
    if (open) {
      fetchOldSchedule();
      setSearch('');
      setPicked(null);
    } else {
      setOldSchedule(null);
    }
  }, [open, fetchOldSchedule]);

  // Filter candidates: exclude old staff itself + anyone already on the event
  const onEventIds = useMemo(() => {
    const ids = new Set();
    (group?.shifts || []).forEach((s) => {
      (s.claimed_by_list || []).forEach((c) => ids.add(c.user_id));
    });
    return ids;
  }, [group]);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter((u) => u.role !== 'admin')
      .filter((u) => u.id !== oldStaff?.user_id && !onEventIds.has(u.id))
      .filter((u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [search, allUsers, oldStaff, onEventIds]);

  const handleConfirm = async () => {
    if (!picked || !oldStaff) return;
    const dates = oldSchedule?.event?.dates || [];
    const totalShifts = oldSchedule?.row?.total_shifts || 0;
    if (totalShifts === 0) {
      if (!window.confirm(`${oldStaff.user_name} has no scheduled shifts on this event yet. Replacing will just swap the slot. Continue?`)) {
        return;
      }
    } else if (!window.confirm(
      `Replace ${oldStaff.user_name} with ${picked.name}?\n\n` +
      `${totalShifts} shift(s) across ${dates.length} day(s) will be transferred with the exact same times.\n` +
      `PENDING shifts stay PENDING.\n\n` +
      `${oldStaff.user_name} will receive a cancellation email.\n` +
      `${picked.name} will receive a schedule email.`
    )) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/replace-staff`, {
        open_shift_ids: group.shifts.map((s) => s.id),
        old_user_id: oldStaff.user_id,
        new_user_id: picked.id,
      });
      toast.success(res.data?.message || 'Replacement complete');
      onReplaced?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Replace failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!oldStaff) return null;

  const dates = oldSchedule?.event?.dates || [];
  const row = oldSchedule?.row;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-barlow tracking-wider uppercase">
            <Replace className="w-5 h-5 text-amber-400" />
            Replace Staff
          </DialogTitle>
          <DialogDescription>
            Swap one staff with another — shift times stay exactly the same.
          </DialogDescription>
        </DialogHeader>

        {/* Being replaced */}
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-amber-400" />
            <span className="text-muted-foreground">Replacing:</span>
            <strong>{oldStaff.user_name}</strong>
            {oldStaff.is_extra && (
              <Badge className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40">+ Extra</Badge>
            )}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading current schedule…
            </div>
          ) : !row ? (
            <p className="text-xs italic text-muted-foreground">No scheduled shifts on this event — the slot will just be passed on.</p>
          ) : (
            <div className="space-y-1 text-xs">
              <div className="text-muted-foreground">
                {row.total_shifts} shift(s), {row.total_hours.toFixed(1)}h total — these will be transferred:
              </div>
              <div className="max-h-32 overflow-auto space-y-0.5 pl-1">
                {dates.map((d) => {
                  const sh = (row.shifts_by_date?.[d] || [])[0];
                  return (
                    <div key={d} className="flex items-center gap-2">
                      <span className="w-20 shrink-0 text-muted-foreground">{formatDateShort(d)}</span>
                      {!sh ? (
                        <span className="italic text-muted-foreground/60">— not scheduled</span>
                      ) : (sh.start_time && sh.end_time) ? (
                        <span className="font-mono">{sh.start_time}–{sh.end_time}</span>
                      ) : (
                        <Badge variant="outline" className="text-[9px] py-0 h-4 border-amber-500/40 text-amber-300">PENDING</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Pick replacement */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Replacement</div>
          {picked ? (
            <div className="flex items-center justify-between p-2 bg-green-500/10 border border-green-500/30 rounded-sm">
              <div className="flex items-center gap-2 text-sm">
                <ArrowRight className="w-4 h-4 text-green-400" />
                <strong>{picked.name}</strong>
                <Badge variant="outline" className="text-[10px]">{picked.role}</Badge>
                {picked.employee_code && (
                  <span className="text-[10px] text-muted-foreground font-mono">#{picked.employee_code}</span>
                )}
              </div>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={() => { setPicked(null); setSearch(''); }}
                data-testid="replace-clear-pick"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-background border-border rounded-sm"
                  data-testid="replace-search-input"
                />
              </div>
              {search && (
                candidates.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic px-1">No staff match — note we exclude anyone already on this event.</p>
                ) : (
                  <div className="max-h-40 overflow-auto space-y-1 border border-border rounded-sm bg-background">
                    {candidates.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => { setPicked(u); setSearch(''); }}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 text-sm hover:bg-amber-500/10 text-left"
                        data-testid={`replace-candidate-${u.id}`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{u.name}</span>
                          <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                        </span>
                        <span className="text-[10px] text-muted-foreground">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>

        <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 px-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
          <span>
            {oldStaff.user_name} will receive a cancellation email. {picked ? picked.name : 'The new staff'} will receive a schedule email with the same times.
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!picked || submitting}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="replace-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Replace className="w-4 h-4 mr-2" />
            )}
            Confirm Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
